"use client"
import React, { useState } from 'react';

// Define component statuses for clearer state management
const STATUS = {
  IDLE: 'idle', // Initial state, form is visible
  LOADING: 'loading', // API call in progress
  WINNER: 'winner', // User is a winner
  LOSER: 'loser', // User is not a winner
  ERROR: 'error', // API or network error
};

const LotteryResults = () => {
  // --- State Management ---
  const [nationalId, setNationalId] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  
  const [status, setStatus] = useState(STATUS.IDLE);
  const [errorMessage, setErrorMessage] = useState('');
  const [winnerData, setWinnerData] = useState(null); // To store winner details

  // --- API Handler ---
  const handleCheckResult = async (e) => {
    e.preventDefault();
    
    setStatus(STATUS.LOADING);
    setErrorMessage('');
    setWinnerData(null);

    try { 
      // Use URL constructor for robust path joining
      const baseUrl = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3000';
      const fullUrl = new URL('lottery_result', baseUrl).href;

      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          national_id: nationalId, 
          phone: phoneNumber,      
        }),
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();

      // Simplified logic based on API response status
      const apiStatus = data.status || data.message;

      if (apiStatus === "invalid_date") {
        setErrorMessage("مهلت شرکت در قرعه‌کشی به پایان رسیده است.");
        setStatus(STATUS.IDLE); // Stay on the form to allow correction or notice
      } else if (apiStatus === "loser") {
        setStatus(STATUS.LOSER);
      }
      // Check for a winner by looking for specific data properties
      else if (data.national_id && data.first_name) {
        setWinnerData(data);
        setStatus(STATUS.WINNER);
      } else {
        // If the response is unknown, treat it as a "loser" state to avoid showing broken UI
        setStatus(STATUS.LOSER);
      }

    } catch (err) {
      console.error("Lottery Check Error:", err);
      setErrorMessage("خطا در برقراری ارتباط با سرور. لطفا اتصال اینترنت خود را بررسی کنید.");
      setStatus(STATUS.ERROR);
    }
  };

  // --- Retry Handler ---
  const handleRetry = () => {
    setStatus(STATUS.IDLE);
    setErrorMessage('');
    setNationalId(""); 
    setPhoneNumber("");
  };

  // Helper to format date
  const formatDate = (dateString) => {
    if (!dateString) return "---";
    return new Date(dateString).toLocaleDateString('fa-IR');
  };

  const isLoading = status === STATUS.LOADING;
  const showForm = status === STATUS.IDLE || status === STATUS.ERROR;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center py-12 px-4" dir="rtl">
      
      {/* Header */}
      <div className="text-center mb-10 mt-8">
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">
          نتایج قرعه کشی برساوش
        </h1>
        <p className="text-gray-500">
          اطلاعات مورد نیاز زیر را وارد کنید
        </p>
      </div>

      {/* Input Form - Shown in idle or error states */}
      {showForm && (
        <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="p-8">
            <form onSubmit={handleCheckResult} className="space-y-6">
              
              {/* National ID */}
              <div>
                <label htmlFor="nid" className="block text-sm font-medium text-slate-700 mb-2">
                 کد ملی
                </label>
                <input
                  id="nid"
                  type="text" 
                  inputMode="numeric"
                  required
                  value={nationalId}
                  onChange={(e) => setNationalId(e.target.value)}
                  placeholder="کد ملی خود را وارد کنید..."
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none transition-all bg-gray-50 focus:bg-white text-right " 
                />
              </div>

              {/* Phone Number */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                  شماره موبایل
                </label>
                <input
                  id="phone"
                  type="tel"
                  required
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="09...."
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none transition-all bg-gray-50 focus:bg-white text-right "
                  dir="ltr" 
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-4 px-6 rounded-lg bg-blue-950 hover:bg-blue-800 text-white font-bold text-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    در حال بررسی...
                  </>
                ) : (
                  "مشاهده نتیجه"
                )}
              </button>
            </form>

            {/* Error Message */}
            {errorMessage && (
              <div className="mt-6 p-4 bg-red-50 text-red-700 rounded-lg text-sm border border-red-200 text-center flex items-center justify-center gap-2">
                <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{errorMessage}</span>

                {/* Allow retry directly from error message */}
                {status === STATUS.ERROR && (
                   <button onClick={handleCheckResult} className="text-red-700 font-bold underline">تلاش مجدد</button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* --- Results Display (Winner or Loser) --- */}
      {(status === STATUS.WINNER || status === STATUS.LOSER) && (
        <div className="w-full max-w-2xl mt-8 animate-fade-in-up">
          
          {/* WINNER STATE */}
          {status === STATUS.WINNER && winnerData ? (
            <div className="bg-white rounded-2xl shadow-lg border-2 border-green-500 overflow-hidden relative">
              <div className="absolute top-0 left-0 w-full h-2 bg-green-500"></div>
              
              <div className="p-8">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-green-600 mb-4">
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  
                  <h2 className="text-2xl font-bold text-slate-800 mb-2">تبریک!</h2>
                  <p className="text-gray-600">
                    شما در قرعه‌کشی برنده شدید.
                  </p>
                </div>

                {/* Table Data Display */}
                {winnerData && (
                  <div className="overflow-hidden border border-gray-200 rounded-xl">
                    <table className="min-w-full divide-y divide-gray-200">

                      <tbody className="bg-white divide-y divide-gray-200">
                        {/* Name */}
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-500 bg-gray-50/50 w-1/3">
                            نام و نام خانوادگی
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                            {winnerData.first_name} {winnerData.last_name}
                          </td>
                        </tr>
                        
                        {/* Car Name */}
 <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-500 bg-gray-50/50">
                            خودرو برنده شده
                          </td>
                          {/* Changed whitespace-nowrap to whitespace-normal to allow text wrapping */}
                          <td className="px-6 py-4 whitespace-normal text-sm text-blue-800 font-bold">
                            <div className='flex flex-col sm:flex-row sm:items-center gap-2'>
                              {winnerData.cars && winnerData.cars.length > 0 ? (
                                <a
                                  href={`/products/${winnerData.cars[0].id}`}
                                  className="bg-gray-200 text-gray-800 py-2 px-4 rounded-lg latinNumbers hover:bg-gray-800 hover:text-white transition-colors inline-block text-center whitespace-nowrap"
                                >
                                  {winnerData.cars[0].english_name}
                                </a>
                              ) : "---"}
                              
                              <p className="text-xs text-gray-500 font-normal leading-tight">
                                جهت مشاهده مشخصات فنی  خودرو روی نام آن کلیک کنید.
                              </p>
                            </div>
                          </td>
                        </tr>

                        {/* National ID */}
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-500 bg-gray-50/50">
                            کد ملی
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dir-ltr text-right">
                            {winnerData.national_id}
                          </td>
                        </tr>

                        {/* Phone */}
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-500 bg-gray-50/50">
                            شماره موبایل
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dir-ltr text-right">
                            {winnerData.phone}
                          </td>
                        </tr>

                         {/* Payment Date */}
                         <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-500 bg-gray-50/50">
                            تاریخ پرداخت
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(winnerData.payment_date)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center items-center">
                    
                  {/* Dynamic Link to Product Page */}
                  {winnerData?.cars && winnerData.cars.length > 0 && (
                    <a 
                     target='_blank'
                      href="https://sale.barsavosh.co/Subscriber/Login"
                      className="text-white bg-blue-900 py-3 px-6 rounded-lg font-semibold hover:bg-blue-800 flex items-center justify-center transition-all shadow-md"
                    >
                      ادامه مراحل دریافت 
                      <svg className="w-4 h-4 mr-1 rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </a>
                  )}
                  
                  {/* Retry Button */}
                  <button 
                    onClick={handleRetry}
                    className="text-gray-600 bg-gray-100 py-3 px-6 rounded-lg font-semibold hover:bg-gray-200 transition-all"
                  >
                    بررسی مجدد
                  </button>
                </div>
              </div>
            </div>
          ) : (
            
            /* LOSER STATE */
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 text-gray-500 mb-6">
                  <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                
                <h2 className="text-2xl font-bold text-slate-800 mb-2">متاسفیم</h2>
                <p className="text-gray-500 leading-relaxed mb-8">
                  متاسفانه کد ملی شما در این مرحله از قرعه‌کشی انتخاب نشده است.
                  امیدواریم در رویدادهای آینده برنده باشید.
                </p>
                
                {/* Retry Button */}
                <button 
                  onClick={handleRetry}
                  className="w-full sm:w-auto text-white bg-slate-900 py-3 px-8 rounded-lg font-semibold hover:bg-slate-800 transition-all shadow-md mb-6"
                >
                  بررسی مجدد
                </button>
                
                <div className="pt-6 border-t border-gray-100">
                   <p className="text-sm text-gray-400">
                     با تشکر از مشارکت شما
                   </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LotteryResults;